import { makeAutoObservable } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { type Contact } from 'entities/Contacts/model/Contact'
import {
  type IIntegrationKey,
  type IResponseCalendlyIntegrationMeeting,
  type IResponseIntegrationMeeting,
  type Integration,
  IntegrationKey,
  IntegrationsApi,
} from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export type MeetingsProps = {
  contact?: Contact | null
  integration?: Integration | null
  onClose?: () => void
  onAddMeetingLink?: (link: string) => void
  onNecessaryReconnect?: (status: boolean) => void
  integrations?: Integration[]
}

export type IIntegrationMeeting = IResponseIntegrationMeeting | IResponseCalendlyIntegrationMeeting

export class MeetingsStore {
  private _contact: Contact | null

  constructor({ contact }: MeetingsProps, private _entities: IEntitiesStore) {
    this._contact = contact || null

    makeAutoObservable(this)
  }

  private _search = ''
  private _loading = false
  private _loadingMore = false
  private _nextPageToken: string | undefined = undefined
  private _requestId = 0
  private _meetings: IIntegrationMeeting[] | null = null
  private _selectedMeeting: IIntegrationMeeting | null = null
  private _selectedIntegration: Integration | null = null
  private _isNecessaryReconnect = false
  private _necessaryReconnectIntegrationKey: IIntegrationKey | undefined = undefined

  loadMeetings = async (integrationKey?: IIntegrationKey, useCalendarApi?: boolean) => {
    const requestId = ++this._requestId
    try {
      this.setLoading(true)

      const activeKey = this.selectedIntegration?.key || integrationKey

      if (
        this.isCalendlySelectedIntegration ||
        integrationKey === IntegrationKey.calendly ||
        useCalendarApi
      ) {
        const { data } = await IntegrationsApi.getCalendlyIntegrationMeetings(
          activeKey ?? IntegrationKey.calendly,
          {
            limit: 100,
            is_active: true,
          }
        )
        if (requestId !== this._requestId) return
        this.setMeetings(data.items ?? [])
        this.setNextPageToken(data.pagination?.next_page_token)
      } else {
        const { data } = await IntegrationsApi.getIntegrationMeetings(
          this.selectedIntegration?.key || IntegrationKey.hubspot
        )
        if (requestId !== this._requestId) return
        if (data) {
          if (data.data) this.setMeetings(data.data)
          if (data.error) {
            this.setNecessaryReconnect()
          }
        }
        this.setNextPageToken(undefined)
      }
    } catch (e) {
      if (requestId !== this._requestId) return
      if (e instanceof AxiosError) {
        toastStore.add({
          title: e.errors.getError('message'),
          type: 'error',
        })
      }
      logger.error(e)
      this.setNecessaryReconnect()
      this.setNextPageToken(undefined)
    } finally {
      if (requestId === this._requestId) this.setLoading(false)
    }
  }

  loadMoreMeetings = async () => {
    if (!this._nextPageToken || this._loadingMore || this._loading) return

    const requestId = this._requestId
    const activeKey = this.selectedIntegration?.key
    const pageToken = this._nextPageToken

    this.setLoadingMore(true)
    try {
      const { data } = await IntegrationsApi.getCalendlyIntegrationMeetings(
        activeKey ?? IntegrationKey.calendly,
        {
          limit: 100,
          is_active: true,
          page_token: pageToken,
        }
      )
      if (requestId !== this._requestId) return
      this.appendMeetings(data.items ?? [])
      this.setNextPageToken(data.pagination?.next_page_token)
    } catch (e) {
      logger.error(e)
    } finally {
      this.setLoadingMore(false)
    }
  }

  private setLoading = (value: boolean) => {
    this._loading = value
  }

  private setLoadingMore = (value: boolean) => {
    this._loadingMore = value
  }

  private setNextPageToken = (token?: string) => {
    this._nextPageToken = token || undefined
  }

  setMeetings = (meetings: IIntegrationMeeting[]) => {
    this._meetings = meetings
  }

  private appendMeetings = (meetings: IIntegrationMeeting[]) => {
    this._meetings = [...(this._meetings ?? []), ...meetings]
  }

  setSearch = async (search: string) => {
    this._search = search
  }

  onSelectMeeting = (meeting: IIntegrationMeeting) => {
    this._selectedMeeting = meeting
    if (this.isCalendlySelectedIntegration) {
      this._entities.eventLogStore.logEvent(
        'Calendly Used',
        {
          event_id: 'calendly_used',
          action: 'Link Added',
          agencyId: this._entities.organizationStore.agency_id,
        },
        { groupId: this._entities.organizationStore.id }
      )
    }
  }

  get isCalendlySelectedIntegration() {
    return this.selectedIntegration?.key === IntegrationKey.calendly
  }

  onBack = () => {
    this._selectedMeeting = null
    this._selectedIntegration = null
    this._search = ''
    this._nextPageToken = undefined
    this._requestId++
    this.setLoading(false)
    this.setLoadingMore(false)
  }

  get hasMoreMeetings() {
    return Boolean(this._nextPageToken)
  }

  get isLoadingMoreMeetings() {
    return this._loadingMore
  }

  onSelectedIntegration = (integration: Integration, useCalendarApi?: boolean) => {
    this._selectedIntegration = integration
    this.loadMeetings(undefined, useCalendarApi)
  }

  setNecessaryReconnect = () => {
    this._isNecessaryReconnect = true
    if (this.selectedIntegration?.key) {
      this._necessaryReconnectIntegrationKey = this.selectedIntegration.key
    }
  }

  get contact() {
    return this._contact
  }

  get isShowBack() {
    return this.selectedMeeting || this.selectedIntegration
  }

  get filteredMeetings() {
    if (!this._search) return this._meetings
    const search = this._search.toLowerCase()

    return this._meetings?.filter((meeting) => {
      if (meeting.name?.toLowerCase().includes(search)) return true
      if ('link' in meeting) {
        return (
          meeting.link?.toLowerCase().includes(search) ||
          meeting.slug?.toLowerCase().includes(search)
        )
      }
      return meeting.scheduling_url?.toLowerCase().includes(search)
    })
  }

  get search() {
    return this._search
  }

  get loading() {
    return this._loading
  }

  get selectedMeeting() {
    return this._selectedMeeting
  }

  get selectedIntegration() {
    return this._selectedIntegration
  }
  get isNecessaryReconnect() {
    return this._isNecessaryReconnect
  }

  get necessaryReconnectIntegrationKey() {
    return this._necessaryReconnectIntegrationKey
  }

  get isCurrentIntegrationNeedReconnect() {
    return this.selectedIntegration?.key === this._necessaryReconnectIntegrationKey
  }
}
